import { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import {
  TextField,
  IconButton,
  Box,
  Typography,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StopIcon from "@mui/icons-material/Stop";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useAuth } from "contexts/auth";
import { useSidebar } from "contexts/sidebar";
import { useInsightsEnabled } from "hooks/use-insights-enabled";
import { InsightsScope } from "hooks/use-insights-scope";
import {
  ALL_WORKSPACES_ID,
  getDevMockWorkspaces,
  getInsightsErrorMessage,
  getWorkspacesForOrganization,
  sendInsightsAgentMessage,
  IInsightsPrefabResponse,
  INSIGHTS_MOCK_ENABLED,
} from "v2/services/insights.service";
import { effectiveInsightsTab } from "./effectiveTab";
import { listsViaSellers, scopeWorkspaces } from "./scopeWorkspaces";
import {
  allowsAllContracts,
  autoSelectedWorkspace,
  listingProblem,
} from "./insightsListing";
import { UserMessage } from "./UserMessage";
import { AssistantMessage, IAssistantContent } from "./AssistantMessage";
import { LoadingMessage } from "./LoadingMessage";
import { ChatSuggestions } from "./ChatSuggestions";
import { QuickQuestions } from "./QuickQuestions";
import { ContractBar } from "./ContractBar";
import { ReportCatalog } from "./reports/ReportCatalog";
import { formatFederalTaxId } from "./formatFederalTaxId";
import {
  ChatContainer,
  MessagesArea,
  InputCard,
  InputToolbar,
  InputDock,
  CenteredColumn,
  EmptyStateContainer,
} from "./ChatMessage.styled";
import {
  CONDUCCO_PRIMARY,
  INSIGHTS_BORDER,
  INSIGHTS_FAINT,
  INSIGHTS_FONT,
  INSIGHTS_MUTED,
  INSIGHTS_TEXT,
} from "./tokens";
import { useAlert } from "contexts/alert";

const INSIGHTS_WORKSPACE_STORAGE_KEY = "@conducco:insightsWorkspaceId";
const INSIGHTS_CHAT_HISTORY_STORAGE_KEY = "@conducco:insightsChatHistory";
const SESSION_MAX_MESSAGES = 5;
const FEATURE_DISABLED_MESSAGE = "Funcionalidade não disponível para esta organização";
// Chips de pergunta pronta acima do input, desligadas a pedido — mantidas no
// código para reativar/reformular depois em vez de recriar do zero.
const QUICK_QUESTIONS_ENABLED = false;
const INSIGHTS_TAB_STORAGE_KEY = "@conducco:insightsTab";

/**
 * As duas superfícies do Insights. Compartilham o workspace escolhido de
 * propósito: quem investiga no chat e baixa o relatório está olhando o mesmo
 * contrato, e trocar de aba não devia trocar o escopo debaixo dele.
 */
export type InsightsTab = "assistente" | "relatorios";

type ChatMessage =
  | { type: "user"; question: string }
  | { type: "assistant"; content: IAssistantContent[] };

interface IWorkspaceChatState {
  messages: ChatMessage[];
  sentCount: number;
  sessionId: string;
}

export interface InsightsPanelProps {
  /**
   * O cliente da tela que abriu o painel. Sem escopo (a rota `/insights` direta)
   * o painel lista todos os workspaces visíveis, como antes.
   */
  scope?: InsightsScope | null;
  initialTab?: InsightsTab;
  /**
   * Quando presente, o painel desenha a barra de topo com o "Voltar". É o que
   * distingue o painel sobreposto da rota `/insights`, que não tem para onde
   * voltar.
   */
  onClose?: () => void;
}

function loadChatHistoryMap(): Record<string, IWorkspaceChatState> {
  try {
    const raw = sessionStorage.getItem(INSIGHTS_CHAT_HISTORY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function InsightsPanel({
  scope,
  initialTab,
  onClose,
}: InsightsPanelProps) {
  const [workspaceId, setWorkspaceId] = useState(
    () =>
      // Aberto de dentro de um contrato, começa nele. Com escopo de cliente, o
      // último workspace usado é ignorado: ele pode ser de outro CNPJ, e o
      // efeito de auto-seleção escolhe o primeiro do cliente (e também troca
      // o contrato da tela se ele não estiver na lista do cliente).
      scope?.workspaceId ||
      (!scope && localStorage.getItem(INSIGHTS_WORKSPACE_STORAGE_KEY)) ||
      ""
  );
  const chatHistoryRef = useRef<Record<string, IWorkspaceChatState> | null>(null);
  if (chatHistoryRef.current === null) {
    chatHistoryRef.current = loadChatHistoryMap();
  }
  const chatHistory = chatHistoryRef.current;
  const didMountRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>(
    () => chatHistory[workspaceId]?.messages ?? []
  );
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sentCount, setSentCount] = useState(
    () => chatHistory[workspaceId]?.sentCount ?? 0
  );
  const [sessionId, setSessionId] = useState(
    () => chatHistory[workspaceId]?.sessionId ?? crypto.randomUUID()
  );
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [tab, setTab] = useState<InsightsTab>(
    () =>
      initialTab ??
      (localStorage.getItem(INSIGHTS_TAB_STORAGE_KEY) === "relatorios"
        ? "relatorios"
        : "assistente")
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { openAlert } = useAlert();
  const {
    enabled: realFlagEnabled,
    qualifyingBuyerIds,
    assistantEnabled: realAssistantEnabled,
    isLoading: isCheckingRealFlag,
  } = useInsightsEnabled();
  // No modo de demonstração a flag por organização não decide nada: o objetivo é
  // ver a tela, e o gate é justamente o que impede isso hoje.
  const flagEnabled = INSIGHTS_MOCK_ENABLED || realFlagEnabled;
  const isCheckingFlag = INSIGHTS_MOCK_ENABLED ? false : isCheckingRealFlag;
  const assistantEnabled = INSIGHTS_MOCK_ENABLED || realAssistantEnabled;

  // A aba que vale, não a que o estado guarda: com o assistente escondido, um
  // `"assistente"` vindo do localStorage (ou do `initialTab`) abriria um chat
  // cujas rotas respondem 403. Forçar aqui é o que torna esconder suficiente.
  const activeTab = effectiveInsightsTab(tab, assistantEnabled);
  const isSeller = Boolean(useAuth().user?.isSeller);
  const allowAll = allowsAllContracts(activeTab, scope, isSeller);

  // Duas origens, porque os usuários partem de pontos diferentes.
  //
  // Corretora com cliente na tela: uma chamada só, com o id daquele CNPJ.
  // Varrer os fornecedores do sidebar não serve aqui — quem está logado como
  // corretora não tem fornecedor nenhum na lista, e o painel abria vazio mesmo
  // com o cliente na tela.
  //
  // RH da empresa cliente, e a rota /insights direta: percorre os fornecedores
  // do sidebar — caminho do usuário comprador. Com o id da própria organização
  // o backend recusa a listagem (400).
  const viaSellers = listsViaSellers(scope);

  const scopedWorkspacesQuery = useQuery({
    queryKey: ["insights-workspaces", scope?.organizationId],
    queryFn: () => getWorkspacesForOrganization(scope!.organizationId),
    enabled: Boolean(scope) && !viaSellers && !INSIGHTS_MOCK_ENABLED,
    retry: false,
  });

  const {
    organizationOptions,
    organizationOptionsFailed,
    isLoading: isLoadingSellers,
  } = useSidebar();

  const workspaceQueries = useQueries({
    queries: !viaSellers
      ? []
      : organizationOptions
          // A própria organização nunca é fornecedora dela mesma: a chamada
          // seria um 400 certo (seller e buyer iguais), e o aviso de lista
          // parcial ficaria aceso para sempre (admin logado no RH vê todas
          // as organizações no menu).
          .filter((seller) => seller.id !== scope?.organizationId)
          .map((seller) => ({
            queryKey: ["insights-workspaces", seller.id],
            queryFn: () => getWorkspacesForOrganization(seller.id),
            retry: false,
          })),
  });

  const isLoadingWorkspaces = INSIGHTS_MOCK_ENABLED
    ? false
    : !viaSellers
    ? scopedWorkspacesQuery.isLoading
    : isLoadingSellers || workspaceQueries.some((query) => query.isLoading);

  const allWorkspaces = !viaSellers
    ? scopedWorkspacesQuery.data ?? []
    : workspaceQueries.flatMap((query) => query.data ?? []);

  // Um seller enxerga todos os buyers que gerencia, mas so os buyers com
  // conector Snowflake na allowlist tem dado consultavel — os outros levariam
  // o Agent a consultar um CNPJ sem nenhuma linha. `qualifyingBuyerIds` vem do
  // proprio gate de feature flag, entao a listagem fica no mesmo escopo do
  // acesso. Sem a lista (buyer logado, ou backend antigo), nao filtra nada.
  //
  // Só no caso sem escopo: com o cliente já escolhido na tela, filtrar por
  // allowlist só conseguiria esvaziar o painel do cliente que o usuário abriu
  // de propósito, e o motivo certo dessa tela é o do card indisponível (D7),
  // não uma lista vazia.
  const enabledWorkspaces =
    !scope && qualifyingBuyerIds.length > 0
      ? allWorkspaces.filter((ws) =>
          qualifyingBuyerIds.includes(ws.buyerOrganizationId)
        )
      : allWorkspaces;

  const enabledIds = enabledWorkspaces.map((workspace) => workspace.id).join(",");
  const workspaces = useMemo(() => {
    if (INSIGHTS_MOCK_ENABLED) {
      return getDevMockWorkspaces(scope?.organizationId ?? "demo");
    }
    return scopeWorkspaces(enabledWorkspaces, scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledIds, scope?.organizationId]);

  // Lista vazia com escopo é o sintoma mais provável de id de organização que
  // não bate com o `buyerOrganizationId` dos contratos. Sem isso, o painel só
  // mostra o alerta e não há como distinguir "cliente sem contrato" de "id
  // errado".
  useEffect(() => {
    if (!import.meta.env.DEV || !scope || isLoadingWorkspaces) return;
    if (workspaces.length > 0) return;
    // eslint-disable-next-line no-console
    console.debug("[insights-panel] sem workspace no escopo:", {
      organizationId: scope.organizationId,
      recebidos: allWorkspaces.map((ws) => ({
        id: ws.id,
        buyerOrganizationId: ws.buyerOrganizationId,
      })),
      erro: viaSellers
        ? workspaceQueries.map((query) => query.error).filter(Boolean)
        : scopedWorkspacesQuery.error,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledIds, scope?.organizationId, isLoadingWorkspaces]);

  const client = workspaces[0];

  const problem = listingProblem({
    scopedFailed: scopedWorkspacesQuery.isError,
    viaSellers,
    sellersFailed: organizationOptionsFailed,
    sweepFailed: workspaceQueries.some((query) => query.isError),
    count: workspaces.length,
  });

  // Espera a lista inteira: ver `autoSelectedWorkspace`.
  useEffect(() => {
    const next = autoSelectedWorkspace({
      workspaceIds: workspaces.map((workspace) => workspace.id),
      current: workspaceId,
      isLoading: isLoadingWorkspaces,
      allowAll,
    });
    if (next) setWorkspaceId(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces.map((workspace) => workspace.id).join(","), isLoadingWorkspaces, allowAll]);

  // "Todos os contratos" existe só no assistente: o backend resolve o escopo
  // cruzado sem schema ali, e recusa `ALL` no caminho dos relatórios (não há
  // união de schemas). Sem isto, ir para a aba de relatórios com "Todos"
  // selecionado devolveria erro em todo card em vez de um relatório.
  useEffect(() => {
    if (activeTab !== "relatorios" || workspaceId !== ALL_WORKSPACES_ID) return;
    if (workspaces.length > 0) setWorkspaceId(workspaces[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, workspaceId, workspaces.length]);

  useEffect(() => {
    // Só o painel sem escopo persiste a escolha: guardar o workspace de um
    // cliente faria a próxima tela abrir no CNPJ errado.
    if (workspaceId && !scope) {
      localStorage.setItem(INSIGHTS_WORKSPACE_STORAGE_KEY, workspaceId);
    }
  }, [workspaceId, scope]);

  useEffect(() => {
    if (!scope) localStorage.setItem(INSIGHTS_TAB_STORAGE_KEY, tab);
  }, [tab, scope]);

  // Restaura o chat salvo do workspace ao trocar (ou reinicia, se nunca visitado).
  // No mount, messages/sentCount/sessionId já vêm inicializados do histórico
  // (useState acima), então esse efeito só deve agir em trocas subsequentes.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!workspaceId) return;
    const saved = chatHistory[workspaceId];
    if (saved) {
      setMessages(saved.messages);
      setSentCount(saved.sentCount);
      setSessionId(saved.sessionId);
    } else {
      setMessages([]);
      setSentCount(0);
      setSessionId(crypto.randomUUID());
    }
  }, [workspaceId]);

  // workspaceId propositalmente fora das deps: no render em que ele muda, ainda
  // carrega messages/sentCount/sessionId do workspace anterior — salvar aqui
  // gravaria o chat errado sob a chave nova. O efeito de restauração acima já
  // dispara um novo render com o estado correto, que então persiste normalmente.
  useEffect(() => {
    if (!workspaceId) return;
    chatHistory[workspaceId] = { messages, sentCount, sessionId };
    sessionStorage.setItem(
      INSIGHTS_CHAT_HISTORY_STORAGE_KEY,
      JSON.stringify(chatHistory)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, sentCount, sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Alguma folha de estilo global do app aplica outline/box-shadow no focus
  // de textarea com especificidade maior que qualquer classe do MUI, mesmo
  // com !important via sx. Isso seta o estilo direto no elemento nativo via
  // CSSStyleDeclaration.setProperty(..., "important"), que ganha de
  // qualquer regra externa, seja ela !important ou não.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    ["outline", "box-shadow", "border"].forEach((prop) => {
      el.style.setProperty(prop, "none", "important");
    });
  }, []);

  const capReached = sentCount >= SESSION_MAX_MESSAGES;
  const isSendActive = Boolean(inputValue.trim()) && !isLoading && !capReached;

  const handleNewConversation = () => {
    setMessages([]);
    setSentCount(0);
    setSessionId(crypto.randomUUID());
  };

  const handleSendMessage = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || isLoading || capReached || !workspaceId) return;

    setMessages((prev) => [...prev, { type: "user", question: trimmed }]);
    setInputValue("");
    setIsLoading(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await sendInsightsAgentMessage(
        {
          content: trimmed,
          workspace_id: workspaceId,
          session_id: sessionId,
        },
        controller.signal
      );

      setMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          content: response.blocks?.length
            ? response.blocks
            : [{ type: "text" as const, text: response.reply }],
        },
      ]);
      setSentCount(response.message_count);
    } catch (error: any) {
      setMessages((prev) => prev.slice(0, -1));

      if (axios.isCancel(error)) return;

      if (error?.response?.data?.message === FEATURE_DISABLED_MESSAGE) {
        setFeatureUnavailable(true);
        return;
      }

      openAlert(getInsightsErrorMessage(error, "Erro ao processar pergunta"), "error");
      if (error?.response?.status === 429) setSentCount(SESSION_MAX_MESSAGES);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancelMessage = () => {
    abortControllerRef.current?.abort();
  };

  const handlePrefabResult = (
    label: string,
    response: IInsightsPrefabResponse
  ) => {
    setMessages((prev) => [
      ...prev,
      {
        type: "assistant",
        content:
          response.rows.length > 0
            ? [{ type: "table" as const, rows: response.rows, label }]
            : [{
                type: "text" as const,
                text: response.message || "Nenhum resultado encontrado.",
                label,
              }],
      },
    ]);
  };

  const handlePrefabError = (message: string) => {
    if (message === FEATURE_DISABLED_MESSAGE) {
      setFeatureUnavailable(true);
      return;
    }
    openAlert(message, "error");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const content = () => {
    if (isCheckingFlag || (isLoadingWorkspaces && workspaces.length === 0)) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress size={22} sx={{ color: CONDUCCO_PRIMARY }} />
        </Box>
      );
    }

    if (!flagEnabled || featureUnavailable) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="info">{FEATURE_DISABLED_MESSAGE}</Alert>
        </Box>
      );
    }

    // Erro na listagem nunca vira "não há contrato" (revisão da #633: o 400 da
    // listagem para usuário buyer era lido como lista vazia; revisão da #642:
    // o mesmo com `/organizations/list` falhando para o RH).
    if (scope && problem === "falhou") {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="warning" sx={{ fontFamily: INSIGHTS_FONT }}>
            {scope.isOwnOrganization
              ? "Não foi possível carregar os contratos da sua empresa agora."
              : "Não foi possível carregar os contratos deste cliente agora."}{" "}
            Tente de novo em instantes; se continuar, avise a Conducco.
          </Alert>
        </Box>
      );
    }

    if (workspaces.length === 0) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="info" sx={{ fontFamily: INSIGHTS_FONT }}>
            {scope?.isOwnOrganization
              ? "Sua empresa ainda não tem contrato com dado consultável no Insights."
              : scope
              ? "Este cliente ainda não tem contrato com dado consultável no Insights."
              : "Nenhum workspace disponível para consulta."}
          </Alert>
        </Box>
      );
    }

    if (activeTab === "relatorios") {
      return <ReportCatalog workspaceId={workspaceId} />;
    }

    return (
      <>
        <ChatContainer>
          <MessagesArea>
          <CenteredColumn sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {messages.length === 0 && !isLoading ? (
              <EmptyStateContainer>
                {/* `h3` em azul ocupava um terço da tela para dizer três
                    palavras, e era o único elemento com cor. */}
                <Typography
                  sx={{
                    fontFamily: INSIGHTS_FONT,
                    fontSize: "1.375rem",
                    fontWeight: 600,
                    color: INSIGHTS_TEXT,
                  }}
                >
                  Como posso ajudar?
                </Typography>
                <Typography
                  sx={{
                    fontFamily: INSIGHTS_FONT,
                    fontSize: "0.8125rem",
                    color: INSIGHTS_MUTED,
                    mt: 0.75,
                    maxWidth: 460,
                  }}
                >
                  Pergunte sobre movimentações, elegibilidade ou exportações do
                  contrato selecionado.
                </Typography>
                <ChatSuggestions
                  workspaceId={workspaceId}
                  disabled={isLoading}
                  onResult={handlePrefabResult}
                  onError={handlePrefabError}
                />
              </EmptyStateContainer>
            ) : (
              <>
                {messages.map((message, index) =>
                  message.type === "user" ? (
                    <UserMessage key={index} question={message.question} />
                  ) : (
                    <AssistantMessage key={index} content={message.content} />
                  )
                )}
                {isLoading && <LoadingMessage />}
                <div ref={messagesEndRef} />
              </>
            )}
          </CenteredColumn>
        </MessagesArea>

        <InputDock>
          <CenteredColumn>
            {QUICK_QUESTIONS_ENABLED && messages.length > 0 && !capReached && (
              <QuickQuestions onSelectQuestion={handleSendMessage} disabled={isLoading} />
            )}

            {capReached && (
              <Alert
                severity="warning"
                sx={{ mb: 1.5 }}
                action={
                  <Button color="inherit" size="small" onClick={handleNewConversation}>
                    Nova conversa
                  </Button>
                }
              >
                Limite atingido
              </Alert>
            )}

            <InputCard>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Digite sua pergunta..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading || capReached}
                variant="standard"
                inputRef={textareaRef}
                spellCheck={false}
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& textarea, & input": {
                    fontFamily: INSIGHTS_FONT,
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "& textarea:focus, & input:focus": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                }}
              />
              <InputToolbar>
                {/* O contrato saiu daqui para a faixa do topo: escondido nesta
                    barra, com um buyer só ele virava texto sem menu e não havia
                    como trocar de contrato. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title="Anexar documento ou imagem (em breve)">
                    <span>
                      <IconButton size="small" disabled>
                        <AddIcon
                          fontSize="small"
                          sx={{ color: `${INSIGHTS_FAINT} !important` }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                <Tooltip
                  title={
                    isLoading
                      ? "Cancelar envio"
                      : `${sentCount}/${SESSION_MAX_MESSAGES} mensagens nesta sessão`
                  }
                >
                  <span>
                    <IconButton
                      onClick={isLoading ? handleCancelMessage : () => handleSendMessage(inputValue)}
                      disabled={isLoading ? false : !inputValue.trim() || capReached}
                      sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        border: `1.5px solid ${CONDUCCO_PRIMARY}`,
                        backgroundColor: isLoading || isSendActive ? CONDUCCO_PRIMARY : "#FFFFFF",
                        transition: "transform 0.15s ease",
                        // Desabilitado com a borda azul cheia parecia clicável;
                        // o cinza diz que falta escrever a pergunta.
                        "&.Mui-disabled": {
                          backgroundColor: "#FFFFFF",
                          borderColor: INSIGHTS_BORDER,
                          "& .MuiSvgIcon-root": { color: `${INSIGHTS_FAINT} !important` },
                        },
                        "&:hover": {
                          backgroundColor: isLoading || isSendActive ? CONDUCCO_PRIMARY : "#FFFFFF",
                          opacity: 0.92,
                          transform: isLoading || isSendActive ? "scale(1.05)" : "none",
                        },
                      }}
                    >
                      {isLoading ? (
                        <StopIcon fontSize="small" sx={{ color: "#FFFFFF !important" }} />
                      ) : (
                        <ArrowUpwardIcon
                          fontSize="small"
                          sx={{
                            color: isSendActive ? "#FFFFFF !important" : `${CONDUCCO_PRIMARY} !important`,
                          }}
                        />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputToolbar>
            </InputCard>
            {/* O contador saiu de dentro do card: ali era uma terceira linha
                dentro da caixa de escrever, e o card crescia sem motivo. */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
              <Typography
                sx={{
                  fontFamily: INSIGHTS_FONT,
                  fontSize: "0.6875rem",
                  color: INSIGHTS_FAINT,
                }}
              >
                {sentCount}/{SESSION_MAX_MESSAGES} mensagens
              </Typography>
            </Box>
          </CenteredColumn>
        </InputDock>
        </ChatContainer>
      </>
    );
  };

  const showChrome = !isCheckingFlag && flagEnabled && !featureUnavailable;

  return (
    <>
      {/* A barra do painel sobreposto. "Voltar" é o primeiro elemento da linha
          porque é o caminho de saída: quem abriu só para tirar uma dúvida lê da
          esquerda para a direita e acha antes de procurar. */}
      {onClose && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, md: 2 },
            px: { xs: 1.5, md: 3 },
            py: 1.25,
            borderBottom: `1px solid ${INSIGHTS_BORDER}`,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={onClose}
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{
              flexShrink: 0,
              fontFamily: INSIGHTS_FONT,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: CONDUCCO_PRIMARY,
              "&:hover": { backgroundColor: "rgba(48,36,225,0.06)" },
            }}
          >
            Voltar
          </Button>

          <Box
            sx={{
              width: "1px",
              height: 24,
              backgroundColor: INSIGHTS_BORDER,
              display: { xs: "none", sm: "block" },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexShrink: 0,
            }}
          >
            <AutoAwesomeIcon
              fontSize="small"
              sx={{ color: `${CONDUCCO_PRIMARY} !important` }}
            />
            <Typography
              sx={{
                fontFamily: INSIGHTS_FONT,
                fontWeight: 600,
                color: CONDUCCO_PRIMARY,
              }}
            >
              Insights
            </Typography>
          </Box>

          {/* Dado fictício sem aviso vira print em apresentação e depois vira
              número citado em reunião. O selo mora do lado do título. */}
          {INSIGHTS_MOCK_ENABLED && (
            <Chip
              size="small"
              label="Demonstração: dados fictícios"
              sx={{
                flexShrink: 0,
                fontFamily: INSIGHTS_FONT,
                fontWeight: 600,
                fontSize: 11,
                color: "#8A4B12",
                backgroundColor: "rgba(224,113,36,0.14)",
              }}
            />
          )}

          {/* Quem é o cliente desta sessão. Em tela cheia a página de origem
              não fica visível atrás — sem isso, nada na tela diz de quem é o
              dado. */}
          {client && (
            <Box sx={{ ml: "auto", minWidth: 0, textAlign: "right" }}>
              <Typography
                noWrap
                sx={{
                  fontFamily: INSIGHTS_FONT,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                {client.buyerName || client.label}
              </Typography>
              <Typography
                noWrap
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontFamily: INSIGHTS_FONT }}
              >
                {[
                  formatFederalTaxId(client.buyerFederalTaxId),
                  `${workspaces.length} ${
                    workspaces.length === 1 ? "contrato" : "contratos"
                  }`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Só depois do gate: sem a flag, o painel é um aviso e não tem aba. E
          com o assistente escondido sobra uma aba só — uma faixa de navegação
          com um destino é ruído, então ela toda sai. */}
      {showChrome && assistantEnabled && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            borderBottom: `1px solid ${INSIGHTS_BORDER}`,
            px: { xs: 2, md: 4 },
            flexShrink: 0,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value: InsightsTab) => setTab(value)}
            TabIndicatorProps={{ sx: { backgroundColor: CONDUCCO_PRIMARY } }}
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                fontFamily: INSIGHTS_FONT,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
                color: "text.secondary",
              },
              "& .Mui-selected": { color: `${CONDUCCO_PRIMARY} !important` },
            }}
          >
            <Tab value="assistente" label="Assistente" />
            <Tab value="relatorios" label="Relatórios" />
          </Tabs>
        </Box>
      )}

      {/* Uma faixa só para as duas abas: o contrato é o mesmo estado nos dois
          lados, e o painel abre já dizendo de quem é o dado.

          `allowAll` só no assistente — o backend resolve o escopo cruzado sem
          schema no caminho do chat, e recusa `ALL` no dos relatórios. */}
      {showChrome && workspaces.length > 0 && (
        <ContractBar
          workspaces={workspaces}
          workspaceId={workspaceId}
          onWorkspaceChange={setWorkspaceId}
          disabled={isLoading}
          allowAll={allowAll}
        />
      )}

      {/* Um fornecedor que falhou some da lista calado: sem este aviso, os
          contratos dele parecem não existir (revisão da #642, F4). */}
      {showChrome && scope && problem === "parcial" && (
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 1.25, flexShrink: 0 }}>
          <Alert severity="warning" sx={{ fontFamily: INSIGHTS_FONT }}>
            Alguns contratos não puderam ser carregados agora e não aparecem na
            lista. Tente de novo em instantes; se continuar, avise a Conducco.
          </Alert>
        </Box>
      )}

      {content()}
    </>
  );
}
